/**
 * DESPESAS: AS REGRAS DA ESCRITA (10/10/2026, medição do item 12). Funções puras que a ExpensesTab usa e os testes
 * cobram dos dois lados. Os textos de confirm seguem a forma do useConfirmarPerigo: o PRIMEIRO parágrafo é o título
 * (a casca o trunca numa linha, então ele é curto), o resto é o corpo.
 */
import type { PeriodFilter } from '../components/PeriodSelector';

const reais = (v: any) => {
  const n = parseFloat(String(v));
  return Number.isFinite(n) ? `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'R$ —';
};
const dataBr = (d: any) => {
  const s = String(d || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s.split('-').reverse().join('/') : '—';
};

/**
 * D2a: excluir ou cancelar diz o que acontece com o dinheiro. No servidor, EXCLUIR apaga os pagamentos (CASCADE) e
 * solta a transação bancária (`expenseId = NULL`) sem desmarcar a conciliação: ela fica "conciliada com nada".
 * CANCELAR tira a despesa do total e deixa os pagamentos e a transação ligados a ela. O confirm dizia só a descrição.
 * `pagamentos` null = não deu para ler: a frase diz isso, nunca "sem pagamento".
 */
export function confirmacaoDeSaidaDaDespesa(acao: 'excluir' | 'cancelar', despesa: any, pagamentos: any[] | null): string {
  const verbo = acao === 'excluir' ? 'Excluir' : 'Cancelar';
  const quem = despesa?.supplier?.name ? ` · ${despesa.supplier.name}` : '';
  // A descrição vai no corpo, não no título: a casca trunca o título numa linha ("FOCUS NF…", prévia de 10/10/2026).
  const linhas: string[] = [
    `${verbo} esta despesa?`,
    `"${despesa?.description || `#${despesa?.id}`}" · ${reais(despesa?.amount)}${quem} · vence ${dataBr(despesa?.dueDate)} · ${despesa?.status || 'sem status'}.`,
  ];
  if (pagamentos === null) {
    linhas.push(acao === 'excluir'
      ? 'Não consegui ler os pagamentos desta despesa. Se houver pagamento conciliado, a transação bancária perde o vínculo e continua marcada como conciliada.'
      : 'Não consegui ler os pagamentos desta despesa. Se houver, eles continuam ligados a ela depois de cancelada.');
  } else if (pagamentos.length > 0) {
    const pago = pagamentos.reduce((s, p) => s + (parseFloat(p?.amount) || 0), 0);
    linhas.push(`Ela tem ${pagamentos.length} ${pagamentos.length === 1 ? 'pagamento' : 'pagamentos'} (${reais(pago)}).`);
    const conciliados = pagamentos.filter((p) => p?.bankTransaction || p?.bankTransactionId);
    if (acao === 'excluir') {
      linhas.push('Os pagamentos são apagados junto.');
      for (const p of conciliados) {
        const t = p.bankTransaction;
        linhas.push(t
          ? `A transação bancária de ${dataBr(t.date)}, ${reais(Math.abs(parseFloat(t.amount)))} ("${t.description || 'sem descrição'}"), perde o vínculo e continua marcada como conciliada.`
          : `A transação bancária #${p.bankTransactionId} perde o vínculo e continua marcada como conciliada.`);
      }
    } else {
      linhas.push(conciliados.length > 0
        ? `Cancelada, ela sai do total, mas os pagamentos e ${conciliados.length === 1 ? 'a transação bancária' : `as ${conciliados.length} transações bancárias`} continuam ligados a ela.`
        : 'Cancelada, ela sai do total, mas os pagamentos continuam registrados nela.');
    }
  } else {
    linhas.push('Ela não tem pagamento registrado.');
  }
  if (acao === 'excluir') linhas.push('Esta ação não pode ser desfeita.');
  return linhas.join('\n\n');
}

/** D4: gerar recorrentes é de UM mês, o da tela. Antes, com vários meses ou por datas, gerava o mês corrente calado. */
export function mesParaGerarRecorrentes(f: PeriodFilter): { mes: string | null; motivo: string | null } {
  if (f.mode === 'month' && f.months.length === 1) return { mes: f.months[0], motivo: null };
  return { mes: null, motivo: 'Gerar recorrentes é de um mês: escolha um mês no período.' };
}

export function confirmacaoDeGerarRecorrentes(mes: string, modelosAtivos: number | null): string {
  const [y, m] = mes.split('-');
  return [
    `Gerar as recorrentes de ${m}/${y}?`,
    modelosAtivos === null
      ? 'Não consegui contar os modelos ativos (a lista de recorrentes não carregou).'
      : `${modelosAtivos} ${modelosAtivos === 1 ? 'modelo ativo' : 'modelos ativos'}. O servidor cria só as que ainda não existem neste mês.`,
  ].join('\n\n');
}

/**
 * D5: a planilha leva o MÊS e o STATUS, mais nada (o servidor não recebe os outros filtros). Por datas ela saía com a
 * base inteira. Devolve a trava (`motivo`) ou o aviso de que os outros filtros não vão (`aviso`).
 */
export function exportacaoDasDespesas(f: PeriodFilter, temOutroFiltro: boolean): { motivo: string | null; aviso: string | null } {
  if (f.mode === 'dateRange') return { motivo: 'Por datas a planilha não recorta: escolha meses no período para exportar.', aviso: null };
  return { motivo: null, aviso: temOutroFiltro ? 'A planilha leva o mês e o status, não os outros filtros desta tela.' : null };
}

/** D6: até a prévia existir no servidor, o modal diz o que a importação faz. */
export const AVISO_DO_CSV =
  'Não há checagem de repetidas: importar o mesmo arquivo duas vezes duplica todas as despesas. Categoria e fornecedor que não existirem com aquele nome são criados.';

/**
 * D7: fatura de cartão com candidatas no mês e nenhuma marcada para substituir. O servidor lista as faturas Pendente/
 * Vencido do mês SEM filtrar pelo cartão, então a frase não afirma que é deste cartão.
 */
export function avisoDaFaturaDoCartao(candidatas: number, substituindo: boolean): string | null {
  if (candidatas === 0 || substituindo) return null;
  return `Há ${candidatas} ${candidatas === 1 ? 'fatura de cartão' : 'faturas de cartão'} neste mês (pode ser de outro cartão). Se uma delas é esta fatura, importar sem substituir DUPLICA a fatura no mês.`;
}

/** D9: "Excluir" fornecedor, categoria e recorrência só DESATIVA no servidor (isActive = false). */
export function confirmacaoDeDesativar(tipo: 'fornecedor' | 'categoria' | 'recorrência', nome: string): string {
  const artigo = tipo === 'fornecedor' ? 'o' : 'a';
  const resto = tipo === 'recorrência'
    ? 'As despesas já geradas ficam. Ela passa para "inativas" e não gera mais.'
    : `As despesas já lançadas com ${artigo === 'o' ? 'ele' : 'ela'} ficam; ${artigo === 'o' ? 'ele sai' : 'ela sai'} das listas de escolha.`;
  const demonstrativo = artigo === 'o' ? 'este' : 'esta';
  // O nome vai no corpo: o título é truncado numa linha pela casca.
  return [`Desativar ${demonstrativo} ${tipo}?`, `"${nome || 'sem nome'}". ${resto}`].join('\n\n');
}
